import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@pazportal/ui';
import {
  useDocumentDownload,
  useDocuments,
  type CustomerDocument,
  type DocumentCategory,
} from '../hooks/useDocuments';
import { useAccount } from '../contexts/AccountContext';
import { SkeletonRow } from '../components/Skeleton';
import { DocumentOpenLink, DocumentOrigin } from '../components/DocumentLink';
import { ScrollRow } from '../components/ScrollRow';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { formatFileSize, formatDate } from '../lib/formatters';
import { DOCUMENT_SECTIONS } from '../config/documentSections';

type CategoryFilter = 'all' | DocumentCategory;

const CATEGORY_OPTIONS: Array<{ value: CategoryFilter; key: MessageKey; hintKey?: MessageKey }> = [
  { value: 'all', key: 'documents.category.all' },
  ...DOCUMENT_SECTIONS,
];

const CATEGORY_ICONS: Record<string, string> = {
  contract: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  invoice: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
  network_diagram: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2z',
  photo: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  floor_plan: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  kb: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  ipam: 'M9 12h6m-3-3v6m-7 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  rack_elevation: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2z',
  website: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
  expiration: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  application: 'M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm4 4h8m-8 4h5',
  computer_asset: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  location: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
  network_device: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
  person: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  vendor: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
  wireless: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0',
  carrier_wan: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 10-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 10-5.368-2.684z',
  telephony: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
};

function DocIcon({ category }: { category: string }) {
  const d = CATEGORY_ICONS[category] ?? CATEGORY_ICONS.contract;
  return (
    <svg className="h-5 w-5 text-text-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={d} />
    </svg>
  );
}

export function DocumentsPage() {
  const { t } = useI18n();
  const { accounts, ready } = useAccount();
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [search, setSearch] = useState('');

  // The site lives in the URL, matching the tickets list: "the drawings for
  // Charlotte" should be a link somebody can send, not a set of clicks to
  // repeat. Category and search stay local — they are ways of looking through
  // a list rather than the list you meant.
  const [params, setParams] = useSearchParams();
  const siteFilter = params.get('site') ?? '';
  const setSiteFilter = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('site', value);
    else next.delete('site');
    setParams(next, { replace: true });
  };

  const { data: documents, isLoading, isError, error } = useDocuments(
    category === 'all' ? undefined : { category },
  );
  const download = useDocumentDownload();
  const [failed, setFailed] = useState<{ id: string; message: string } | null>(null);

  // Only sites that actually hold a document; a filter full of empty options is
  // worse than none. Portfolio-level papers have no site and are always shown.
  const sitesWithDocuments = useMemo(() => {
    const byId = new Map<string, string>();
    for (const doc of documents ?? []) {
      if (doc.location_id && doc.location?.name) byId.set(doc.location_id, doc.location.name);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [documents]);

  // A link whose site no longer holds anything falls back to everything rather
  // than showing an empty page beside a control naming a site not in its list.
  const siteIsStale = !!siteFilter && !sitesWithDocuments.some((site) => site.id === siteFilter);
  const activeSite = siteIsStale ? '' : siteFilter;

  const bySite = activeSite
    ? documents?.filter((d) => d.location_id === activeSite)
    : documents;

  const filtered = search.trim()
    ? bySite?.filter((d) => d.title.toLowerCase().includes(search.trim().toLowerCase()))
    : bySite;

  const startDownload = (doc: CustomerDocument) => {
    setFailed(null);
    download.mutate(doc, {
      onError: (err) => setFailed({ id: doc.id, message: err.message }),
    });
  };

  const categoryLabel =
    category === 'all'
      ? null
      : t(CATEGORY_OPTIONS.find((c) => c.value === category)?.key ?? 'documents.category.all');

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold text-text">{t('documents.title')}</h1>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
        {sitesWithDocuments.length > 1 && (
          <div className="flex items-center gap-2">
            <label htmlFor="document-site-filter" className="text-xs text-text-muted">
              {t('tickets.filter_site')}
            </label>
            <select
              id="document-site-filter"
              value={activeSite}
              onChange={(event) => setSiteFilter(event.target.value)}
              className="h-9 w-full rounded-sm border border-border bg-surface px-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-primary sm:w-auto"
            >
              <option value="">{t('documents.filter_all_sites')}</option>
              {sitesWithDocuments.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="w-full sm:w-64">
          <span className="sr-only">{t('documents.search')}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('documents.search')}
            className="min-h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary sm:min-h-9"
          />
        </label>
        </div>
      </div>

      {/* These are filters, not tabs. A tablist promises each control owns a
          panel and that arrow keys move between them; this list narrows one
          list in place. Radios say exactly that and behave correctly on a
          keyboard without pretending to a contract they do not keep. */}
      <ScrollRow
        role="radiogroup"
        aria-label={t('documents.filter_label')}
        className="-mx-3 flex gap-2 px-3 pb-1 sm:mx-0 sm:px-0"
      >
        {CATEGORY_OPTIONS.map((cat) => (
          <button
            key={cat.value}
            type="button"
            role="radio"
            aria-checked={category === cat.value}
            onClick={() => setCategory(cat.value)}
            title={cat.hintKey ? t(cat.hintKey) : undefined}
            className={`min-h-11 whitespace-nowrap rounded-lg px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-9 ${
              category === cat.value
                ? 'bg-primary text-text-inverse'
                : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
            }`}
          >
            {t(cat.key)}
          </button>
        ))}
      </ScrollRow>

      {!ready || isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState icon={<DocIcon category="contract" />} title={t('account.none')} subtitle={t('account.none_hint')} />
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-notification bg-notification-soft p-5 text-sm text-notification">
          {t('documents.error')} {error instanceof Error ? error.message : ''}
        </div>
      ) : !filtered?.length ? (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<DocIcon category={category === 'all' ? 'contract' : category} />}
            title={
              categoryLabel
                ? t('documents.empty_category', { category: categoryLabel.toLowerCase() })
                : t('documents.empty')
            }
            subtitle={t('documents.empty_hint')}
          />
        </div>
      ) : (
        <ul className="divide-y divide-divider rounded-lg border border-border bg-surface">
          {filtered.map((doc) => {
            const isBusy = download.isPending && download.variables?.id === doc.id;
            return (
              <li key={doc.id} className="px-4 py-3 sm:px-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <DocIcon category={doc.category} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text">{doc.title}</p>
                      <p className="truncate text-xs text-text-faint">
                        {doc.location?.name ?? t('documents.all_locations')} &middot;{' '}
                        <DocumentOrigin doc={doc} fileSize={formatFileSize(doc.file_size_bytes)} /> &middot;{' '}
                        {formatDate(doc.created_at)}
                      </p>
                    </div>
                  </div>
                  {doc.external_url ? (
                    <DocumentOpenLink href={doc.external_url} />
                  ) : doc.s3_key && (
                    <button
                      type="button"
                      onClick={() => startDownload(doc)}
                      disabled={isBusy}
                      className="inline-flex min-h-11 flex-shrink-0 items-center rounded-md px-2 text-sm font-medium text-primary hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
                    >
                      {isBusy ? t('common.loading') : t('documents.download')}
                    </button>
                  )}
                </div>
                {failed?.id === doc.id && (
                  <p role="alert" className="mt-1 text-xs text-notification">
                    {t('documents.download_failed')}: {failed.message}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
